// pages/signature/modals/tabs/EnvelopeTabSignatarios.tsx
import { Box, Button, CircularProgress, Grid, Paper, Stack, Typography, alpha } from "@mui/material";
import { useEffect, useRef } from "react";
import { IVFormErros, VForm, VTextField, useVForm } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { globalMaskDocument } from "../../../../shared/utils";
import * as translateUtils from "../../../../shared/utils/globalTranslateSignature";
import type { ISignatario } from "../../types/types";
import CardSigner, { ParticipantType } from "./CardSigner";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { useEnvelopeParticipants } from "../../hooks/useEnvelopeParticipants";
import { FormHandles } from "@unform/core";
import * as yup from "yup";
import { DisabledOverlay } from "../../components/DisabledOverlay";

interface Props {
	active: boolean;
	envelopeId?: string;
	signers: ISignatario[];
	observers: ISignatario[];
	onChange: (nextSigners: ISignatario[], nextObservers: ISignatario[]) => void;
	envelopeStatus?: string;
}

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();
const translateStatus = (s: string) =>
	typeof translateUtils.globalTranslateSignatureStatus === "function" ? translateUtils.globalTranslateSignatureStatus(s) : s;
const isSigned = (s?: string | null) => norm(translateStatus(s || "")) === "signed";

export default function EnvelopeTabSignatarios({ active, envelopeId, signers, observers, onChange, envelopeStatus }: Props) {
	const formRef = useRef<FormHandles>(null);

	const {
		safeSigners,
		safeObservers,
		totalCountSignersOnly,
		inputsDisabled,
		allowSignerWitness,
		allowObserver,
		defaultTypeForForm,
		isLoadingDocument,
		isSubmitting,
		handleAddParticipant,
		handleRemoveParticipant,
		handleResendLink,
		handleCpfBlur,
	} = useEnvelopeParticipants({ envelopeId, signers, observers, onChange, envelopeStatus });

	useEffect(() => {
		if (!active) return;
		requestAnimationFrame(() => {
			formRef.current?.setData?.({ document: "", name: "", email: "", type: defaultTypeForForm });
			formRef.current?.setErrors?.({});
		});
	}, [active, defaultTypeForForm, formRef]);

	const handleFormSubmit = async () => {
		const values = formRef.current?.getData?.() || {};
		const res = await handleAddParticipant(values);

		if (res === true) {
			formRef.current?.setData?.({ document: "", name: "", email: "", type: defaultTypeForForm });
			formRef.current?.setErrors?.({});
		} else if (res instanceof yup.ValidationError) {
			const validationErrors: IVFormErros = {};
			res.inner.forEach((err) => {
				if (err.path) validationErrors[err.path] = err.message;
			});
			formRef.current?.setErrors?.(validationErrors);
		} else {
			formRef.current?.setErrors?.({});
		}
	};

	const handleCpfBlurWrapper = async () => {
		const values = formRef.current?.getData?.() || {};
		const name = await handleCpfBlur(values?.document);
		if (name) {
			requestAnimationFrame(() => {
				formRef.current?.setFieldValue?.("name", name);
			});
		}
	};

	if (!envelopeId) return <DisabledOverlay />;

	return (
		<Stack spacing={2}>
			{/* Form */}
			<Box component={Paper} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Adicionar participante
				</Typography>

				<VForm
					ref={formRef}
					onSubmit={handleFormSubmit}
					onPointerEnterCapture={undefined as any}
					onPointerLeaveCapture={undefined as any}
					placeholder={undefined as any}
				>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={3}>
							<VMaskTextField
								name="document"
								mask="cpf"
								label="CPF"
								fullWidth
								onBlur={handleCpfBlurWrapper}
								disabled={inputsDisabled}
								FormHelperTextProps={{ style: { height: "1em" } }} // ✅ CORREÇÃO: Garante espaço para a mensagem de erro
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<VTextField
								name="name"
								label="Nome"
								fullWidth
								disabled={inputsDisabled}
								FormHelperTextProps={{ style: { height: "1em" } }} // ✅ CORREÇÃO: Garante espaço para a mensagem de erro
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<VTextField
								name="type"
								label="Tipo"
								select
								fullWidth
								SelectProps={{ native: true }}
								disabled={inputsDisabled}
								FormHelperTextProps={{ style: { height: "1em" } }} // ✅ CORREÇÃO: Garante espaço para a mensagem de erro
							>
								<option value="signer" disabled={!allowSignerWitness}>
									Assinante {allowSignerWitness ? "" : "(apenas em Rascunho)"}
								</option>
								<option value="witness" disabled={!allowSignerWitness}>
									Testemunha {allowSignerWitness ? "" : "(apenas em Rascunho)"}
								</option>
								<option value="observers" disabled={!allowObserver}>
									Observador {allowObserver ? "" : "(indisponível neste status)"}
								</option>
							</VTextField>
						</Grid>
						<Grid item xs={12} sm={8}>
							<VTextField
								name="email"
								label="E-mail"
								fullWidth
								disabled={inputsDisabled}
								FormHelperTextProps={{ style: { height: "1em" } }} // ✅ CORREÇÃO: Garante espaço para a mensagem de erro
							/>
						</Grid>

						<Grid item xs={12} sm="auto" sx={{ display: "flex", alignItems: "flex-end" }}>
							{" "}
							{/* ✅ CORREÇÃO: Alinha o botão corretamente */}
							<Stack direction="row" justifyContent="flex-end" spacing={1}>
								<Button type="submit" variant="contained" disabled={isSubmitting || inputsDisabled}>
									{isSubmitting ? (
										<>
											<CircularProgress size={16} sx={{ mr: 1 }} /> Adicionando…
										</>
									) : (
										"Adicionar"
									)}
								</Button>
							</Stack>
						</Grid>
					</Grid>
				</VForm>
			</Box>

			{/* Lista com CardSigner */}
			<Box component={Paper} variant="outlined" sx={{ p: 2, borderRadius: 2, position: "relative" }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Participantes
				</Typography>
				<Box
					sx={{
						maxHeight: { xs: 360, sm: 420, md: 480 },
						overflowY: "auto",
						pr: 0.5,
						"&::-webkit-scrollbar": { width: 8 },
						"&::-webkit-scrollbar-thumb": (t) => ({ backgroundColor: t.palette.divider, borderRadius: 8 }),
					}}
				>
					{/* Assinantes + Testemunhas */}
					<Typography variant="overline" sx={{ px: 0.5, color: "text.secondary" }}>
						Assinantes e testemunhas ({totalCountSignersOnly})
					</Typography>
					<Grid container spacing={1.25} sx={{ mb: 2 }}>
						{safeSigners.map((p) => {
							const t: ParticipantType = p.type === "witness" ? "witness" : "signer";
							return (
								<Grid key={p.id} item xs={12} md={6}>
									<CardSigner
										name={p.name}
										email={p.email}
										documentMasked={globalMaskDocument(p.document ?? "")}
										type={t}
										status={translateStatus(p.status || "")}
										dense
										canResend={!isSigned(p.status)}
										canRemove={!isSigned(p.status)}
										onResendLink={() => handleResendLink(p)}
										onRemove={() => handleRemoveParticipant(p)}
									/>
								</Grid>
							);
						})}
						{safeSigners.length === 0 && (
							<Grid item xs={12}>
								<Typography variant="body2" color="text.secondary">
									Nenhum assinante/testemunha.
								</Typography>
							</Grid>
						)}
					</Grid>

					{/* Observadores */}
					<Typography variant="overline" sx={{ px: 0.5, color: "text.secondary" }}>
						Observadores ({safeObservers.length})
					</Typography>
					<Grid container spacing={1.25}>
						{safeObservers.map((p) => (
							<Grid key={p.id} item xs={12} md={6}>
								<CardSigner
									name={p.name}
									email={p.email}
									documentMasked={globalMaskDocument(p.document ?? "")}
									type="observers"
									dense
									canResend={false}
									canRemove
									onRemove={() => handleRemoveParticipant(p)}
								/>
							</Grid>
						))}
						{safeObservers.length === 0 && (
							<Grid item xs={12}>
								<Typography variant="body2" color="text.secondary">
									Nenhum observador.
								</Typography>
							</Grid>
						)}
					</Grid>
				</Box>
			</Box>
			<LoadingDialog isLoading={isLoadingDocument} loadingText={`Localizando documento...`} />
		</Stack>
	);
}
